import { NessieThemeColors } from "@classdojo/web/nessie/components/theme";
import { TranslatedString } from "@classdojo/web/pods/i18n/translate";
import combineActionHandlers from "@web-monorepo/infra/combineActionHandlers";
import { PodInstallFunction } from "@web-monorepo/shared/podInfra";
import { autoTranslate } from "@web-monorepo/vite-auto-translate-plugin/runtime";
import { BoardSlice, selectBoards } from "pods/board";
import { Question } from "src/pods/pairshare/types";
import logo from "./assets/logo.svg";
import { selectBoard, selectBoardHandler, unselectBoard, unselectBoardHandler } from "./data/board";
import {
  receiveIdeasHandler,
  RECEIVE_IDEAS,
  sendHideVideo,
  sendIdeasHandler,
  sendSeekOffset,
  sendVideoIsFullscreen,
  sendVideoPlaybackState,
  SEND_IDEAS,
} from "./data/display";
import { Slide } from "./data/types";

type AppMetadata = {
  title: TranslatedString;
  icon: string;
  backgroundColor: NessieThemeColors;
};

export const appMetadata = (): AppMetadata => ({
  title: autoTranslate("Mojo Show"),
  icon: logo,
  backgroundColor: "dt_background_primary",
});

const STATE_KEY = "ideas";

export type Display = {
  watch: Slide | null;
  question: Question | null;
  isPlaying: boolean;
  isFullscreen: boolean;
  seekOffset: number;
};

export type IdeasState = {
  selectedBoardSlug: string | null;
  display: Display;
};

export type IdeasSlice = {
  [STATE_KEY]: IdeasState;
};

const initialState: IdeasState = {
  selectedBoardSlug: null,
  display: {
    watch: null,
    question: null,
    isPlaying: false,
    isFullscreen: false,
    seekOffset: 0,
  },
};

// selectors
export const selectIsPlaying = (state?: IdeasSlice | null): boolean => state?.[STATE_KEY]?.display?.isPlaying || false;
export const selectSeekOffset = (state?: IdeasSlice | null): number => state?.[STATE_KEY]?.display?.seekOffset || 0;

export const selectBoardSlugs = (state: IdeasSlice & BoardSlice) => {
  return selectBoards(state).map(({ slug }) => slug);
};

export const selectBoardBySlug = (state: IdeasSlice & BoardSlice, boardSlug: string) => {
  const boards = selectBoards(state);
  return boards.find((board) => board.slug === boardSlug);
};

export {
  SEND_IDEAS,
  RECEIVE_IDEAS,
  selectBoard,
  unselectBoard,
  sendHideVideo,
  sendVideoIsFullscreen,
  sendVideoPlaybackState,
  sendSeekOffset,
};

const install: PodInstallFunction = (installReducer) => {
  installReducer(
    STATE_KEY,
    combineActionHandlers(initialState, [
      selectBoardHandler,
      unselectBoardHandler,
      sendIdeasHandler,
      receiveIdeasHandler,
    ]),
  );
};

export default install;
